import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MAINTENANCE_CLEAR_EVENT_NAME,
  MAINTENANCE_EVENT_NAME,
} from './maintenanceBus';
import {
  fetchMaintenanceMagazine,
  fetchMaintenancePublicState,
  readMaintenanceMagazineCache,
  readMaintenancePageCache,
  writeMaintenanceMagazineCache,
  writeMaintenancePageCache,
} from './maintenancePageApi';

const ACTIVE_POLL_MS = 3_000;
const IDLE_POLL_MS = 5_000;
const STORED_ACTIVE_GRACE_MS = 12 * 60 * 60 * 1000;

function normalizeState(raw, receivedAt = Date.now()) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const plan = source.plan && typeof source.plan === 'object' ? source.plan : null;
  const controllerTime = Number(source.controllerTime);
  const clockOffsetMs = Number.isFinite(controllerTime) ? controllerTime - receivedAt : 0;
  return {
    active: Boolean(source.active),
    scheduled: Boolean(source.scheduled),
    plan,
    magazineMeta: source.magazine && typeof source.magazine === 'object' ? source.magazine : null,
    controllerTime: Number.isFinite(controllerTime) ? controllerTime : receivedAt,
    clockOffsetMs,
    receivedAt,
    source: 'public-api',
  };
}

function validCachedState(value) {
  if (!value || typeof value !== 'object') return null;
  if (!value.active) return value;
  const endMs = Date.parse(value.plan?.expectedEndAt || '');
  if (Number.isFinite(endMs) && Date.now() - endMs > STORED_ACTIVE_GRACE_MS) return null;
  return value;
}

function planFromSocket(detail = {}, previous = null) {
  const startAt = detail.start_at || detail.startAt || previous?.startsAt || null;
  const expectedEndAt = detail.end_at || detail.endAt || detail.expected_end_at || detail.expectedEndAt || previous?.expectedEndAt || null;
  const stage = String(detail.operations_phase || detail.operationsPhase || detail.phase || previous?.stage || '').toUpperCase();
  return {
    ...(previous || {}),
    id: detail.maintenance_id || detail.maintenanceId || detail.schedule_id || detail.scheduleId || previous?.id || null,
    title: detail.title || previous?.title || '',
    status: detail.mode || previous?.status || 'RUNNING',
    stage,
    startsAt: startAt,
    expectedEndAt,
    progress: Number(detail.progress ?? previous?.progress ?? 0),
  };
}

export function useMaintenancePageState() {
  const [state, setState] = useState(() => validCachedState(readMaintenancePageCache()) || {
    active: false,
    scheduled: false,
    plan: null,
    magazineMeta: null,
    controllerTime: Date.now(),
    clockOffsetMs: 0,
    receivedAt: Date.now(),
    source: 'initial',
  });
  const [magazine, setMagazine] = useState(() => readMaintenanceMagazineCache());
  const timerRef = useRef(null);
  const startActivationTimerRef = useRef(null);
  const requestRef = useRef(null);

  const refreshMagazine = useCallback(async (meta) => {
    if (!meta?.enabled) {
      setMagazine(null);
      writeMaintenanceMagazineCache(null);
      return;
    }
    const cached = readMaintenanceMagazineCache();
    if (cached?.updatedAt && meta.updatedAt && cached.updatedAt === meta.updatedAt) {
      setMagazine(cached);
      return;
    }
    try {
      const next = await fetchMaintenanceMagazine();
      const normalized = next?.enabled ? {
        enabled: true,
        html: String(next.html || ''),
        css: String(next.css || ''),
        updatedAt: next.updatedAt || meta.updatedAt || null,
      } : null;
      setMagazine(normalized);
      writeMaintenanceMagazineCache(normalized);
    } catch (_) {
      if (cached) setMagazine(cached);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (requestRef.current) return requestRef.current;
    requestRef.current = (async () => {
      try {
        const receivedAt = Date.now();
        const raw = await fetchMaintenancePublicState();
        const next = normalizeState(raw, receivedAt);
        setState(next);
        writeMaintenancePageCache(next);
        if (next.active) await refreshMagazine(next.magazineMeta);
        else {
          setMagazine(null);
          writeMaintenanceMagazineCache(null);
        }
        return next;
      } catch (_) {
        return null;
      } finally {
        requestRef.current = null;
      }
    })();
    return requestRef.current;
  }, [refreshMagazine]);

  useEffect(() => {
    let cancelled = false;
    const schedule = (delay) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        const next = await refresh();
        if (cancelled) return;
        schedule(next?.active ? ACTIVE_POLL_MS : IDLE_POLL_MS);
      }, delay);
    };
    refresh().then((next) => {
      if (!cancelled) schedule(next?.active ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // 예약 상태를 한 번 받아두면 30초 폴링을 기다리지 않고,
  // 브라우저가 startsAt 도달 순간 자체 타이머로 점검 화면을 즉시 활성화한다.
  // Controller와 브라우저 시계 차이는 마지막 public API 응답의 clockOffsetMs를 사용한다.
  useEffect(() => {
    clearTimeout(startActivationTimerRef.current);
    startActivationTimerRef.current = null;

    if (!state?.scheduled || state?.active || !state?.plan?.startsAt) return undefined;

    const expectedPlanId = state.plan.id || null;
    const expectedStartsAt = state.plan.startsAt;
    const startMs = Date.parse(expectedStartsAt);
    if (!Number.isFinite(startMs)) return undefined;

    let disposed = false;
    const arm = () => {
      if (disposed) return;
      const controllerNow = Date.now() + Number(state.clockOffsetMs || 0);
      const remaining = startMs - controllerNow;

      if (remaining <= 0) {
        setState((previous) => {
          if (!previous || previous.active || !previous.scheduled) return previous;
          if ((previous.plan?.id || null) !== expectedPlanId || previous.plan?.startsAt !== expectedStartsAt) return previous;

          const localNow = Date.now();
          const next = {
            ...previous,
            active: true,
            scheduled: false,
            plan: {
              ...(previous.plan || {}),
              status: 'RUNNING',
              // 실제 Controller 단계가 다음 API/Socket 응답으로 곧 동기화되기 전까지
              // 로컬 시작 도달임을 명확히 표시한다.
              stage: 'START_REACHED',
            },
            controllerTime: localNow + Number(previous.clockOffsetMs || 0),
            receivedAt: localNow,
            source: 'local-start-timer',
          };
          writeMaintenancePageCache(next);
          return next;
        });

        // 점검 시작 직후 실제 Controller 단계/종료시간/매거진을 즉시 재동기화한다.
        void refresh();
        return;
      }

      // setTimeout 최대값/백그라운드 장시간 예약 문제를 피하면서,
      // 시작 1분 전부터는 정확한 남은 지연값으로 다시 arm 된다.
      const delay = Math.max(1, Math.min(remaining, 60_000));
      startActivationTimerRef.current = setTimeout(arm, delay);
    };

    arm();
    return () => {
      disposed = true;
      clearTimeout(startActivationTimerRef.current);
      startActivationTimerRef.current = null;
    };
  }, [
    state?.active,
    state?.scheduled,
    state?.plan?.id,
    state?.plan?.startsAt,
    state?.clockOffsetMs,
    refresh,
  ]);

  useEffect(() => {
    const onMaintenance = (event) => {
      const detail = event?.detail || {};
      const phase = String(detail.phase || '').toLowerCase();
      if (phase === 'completed' || phase === 'cancelled') {
        const cleared = {
          active: false,
          scheduled: false,
          plan: null,
          magazineMeta: null,
          controllerTime: Date.now(),
          clockOffsetMs: 0,
          receivedAt: Date.now(),
          source: 'socket-terminal',
        };
        setState(cleared);
        setMagazine(null);
        writeMaintenancePageCache(cleared);
        writeMaintenanceMagazineCache(null);
        void refresh();
        return;
      }
      const now = Date.now();
      setState((previous) => {
        const plan = planFromSocket(detail, previous?.plan);
        const startsMs = Date.parse(plan.startsAt || '');
        const active = Number.isFinite(startsMs) ? now >= startsMs : ['join_blocked', 'safe_stop'].includes(phase);
        const next = {
          ...(previous || {}),
          active,
          scheduled: !active,
          plan,
          controllerTime: now,
          clockOffsetMs: 0,
          receivedAt: now,
          source: 'socket',
        };
        writeMaintenancePageCache(next);
        return next;
      });
      // 점검 시작/종료시간 변경 이벤트를 받으면 공개 상태와 매거진 버전도 즉시 동기화한다.
      void refresh();
    };
    const onClear = () => refresh();
    window.addEventListener(MAINTENANCE_EVENT_NAME, onMaintenance);
    window.addEventListener(MAINTENANCE_CLEAR_EVENT_NAME, onClear);
    return () => {
      window.removeEventListener(MAINTENANCE_EVENT_NAME, onMaintenance);
      window.removeEventListener(MAINTENANCE_CLEAR_EVENT_NAME, onClear);
    };
  }, [refresh]);

  return useMemo(() => ({ ...state, magazine, refresh }), [state, magazine, refresh]);
}
